<template>
    <FloatLabel variant="on">
        <AutoComplete
            ref="autoCompleteRef"
            v-model="computedName"
            :suggestions="suggestionNames"
            :placeholder
            auto-option-focus
            typeahead
            fluid
            @complete="search"
            @option-select="onOrgSelected"
            @keydown.enter.prevent
        >
            <template #option="slotProps">
                <div class="flex gap-2 items-baseline" :data-cy="slotProps.option">
                    <i class="i-ph-building text-primary"/>
                    <div>{{ slotProps.option }} <span class="text-sm opacity-60">{{ getFullObjectFromName(slotProps.option)?.number }}</span></div>
                </div>
            </template>
        </AutoComplete>

        <label/>
    </FloatLabel>
</template>

<script setup lang="ts">
import {AutoComplete, AutoCompleteOptionSelectEvent, FloatLabel} from 'primevue'
import {computed, ref} from 'vue'
import {getBrgSearch} from '@tritt/brg-search-client-ts'
import {Organization} from '@tritt/brg-search-client-ts/lib/types'

const brgSearch = getBrgSearch()

const props = defineProps({
    modelValue: {
        type: String,
        default: ''
    },
    placeholder: {
        type: String,
        default: 'Skriv navn eller søk etter bedrift'
    }
})

const emit = defineEmits(['orgSelected', 'update:modelValue'])

const autoCompleteRef = ref(null)

const computedName = computed({
    get: () => props.modelValue + '',
    set: (option: Organization) => {
        if (option.name) {
            emit('update:modelValue', option.name)
        } else {
            emit('update:modelValue', option as unknown as string)
        }
    }
})

const suggestions = ref<Organization[]>([])

const suggestionNames = computed(() => {
    return suggestions.value.map(org => org.name)
})

const getFullObjectFromName = (name: string) => {
    return suggestions.value.find(org => org.name === name)
}

const search = async (event: { query: string }) => {
    const searchValue = event.query.trim()
    if (searchValue.length < 3) {
        return
    }
    const searchResults = await brgSearch.search(searchValue)

    suggestions.value = [...searchResults]
}

const onOrgSelected = (event: AutoCompleteOptionSelectEvent) => {
    const org = getFullObjectFromName(event.value)
    emit('orgSelected', org)
}

defineExpose({
    focus: () => {
        const autoComplete = autoCompleteRef.value as any
        if (!autoComplete && !props.modelValue) return

        const inputEl = autoComplete.$el?.querySelector('input')

        if (inputEl) {
            const mockEvent = {
                target: inputEl,
                type: 'focus'
            } as FocusEvent
            autoComplete.search(mockEvent, props.modelValue, 'input')
        }
    }
})

</script>

<style scoped>

</style>